import { toast } from 'react-toastify'

import { pipe } from 'utils'
import {
  UPLOADED_ORG_ATTENDANTS_REQUIRED_FIELDS,
  UPLOADED_ORG_ATTENDANTS_ROLES,
} from 'resources'
import { orgAttendantsFileErrorMessagesCodes } from 'components/PosAccounts/utils'

/**
 * Adds custom error messages to file upload errors.
 *
 * @param {Array<Object>} errors - The array of original error objects.
 * @param {Array<Object>} customErrors - The array of custom error objects with custom messages.
 * @param {string} errors[].code - The error code of the original error.
 * @param {string} errors[].message - The error message of the original error.
 * @param {string} customErrors[].code - The error code of the custom error.
 * @param {string} customErrors[].message - The custom error message.
 * @returns {Array<Object>} The array of error objects with custom messages applied where applicable.
 */
const addCustomErrorsOnFileUpload = (
  errors = [],
  customErrors = []
) => {
  return errors.map((error) => {
    const customError = customErrors.find(
      (customError) => customError.code === error.code
    )

    return customError
      ? {
          ...error,
          message: customError.message,
        }
      : error
  })
}

/**
 * Displays errors for rejected files using toast notifications.
 *
 * @param {Array} [fileRejections=[]] - An array of file rejection objects.
 * @param {Array} fileRejections[].errors - An array of error objects for each rejected file.
 * @param {string} fileRejections[].errors[].message - The error message for each error.
 */
const showUploadedFileErrors = (fileRejections = []) => {
  // Extract error messages and error codes from rejected files
  const extractedErrors = fileRejections
    .map(({ errors }) => errors)
    .flat()

  const errorsWithCustomErrors = addCustomErrorsOnFileUpload(
    extractedErrors,
    [
      {
        code: 'file-invalid-type',
        message: 'Please upload a CSV file.',
      },
    ]
  )

  // Get all error messages from rejected files
  const errors = errorsWithCustomErrors?.map(({ message }) => message)

  if (errors.length) {
    // Remove duplicated errors
    const uniqueErrors = [...new Set(errors)]
    uniqueErrors.forEach((error) => {
      toast.error(error)
    })

    return
  }
}

/**
 * Validates the file headers to ensure all required headers are present.
 *
 * @param {Array<string>} headers - The list of headers from the uploaded file.
 * @returns {boolean} - Returns true if all required headers are present, otherwise false.
 */
const validateFileHeaders = (headers = []) => {
  const requiredHeaders = UPLOADED_ORG_ATTENDANTS_REQUIRED_FIELDS

  const missingHeaders = requiredHeaders.filter(
    (requiredHeader) => !headers.includes(requiredHeader)
  )

  if (missingHeaders.length) {
    return false
  }

  return true
}

/**
 * Creates a function to validate that required fields are present in each uploaded attendant object.
 *
 * @param {Array<string>} requiredFields - An array of required field names.
 * @returns {Function} A function that takes an array of uploaded attendants and returns the validated attendants.
 */
const validateCorrectHeaders =
  (requiredFields) =>
  /**
   * Validates that required fields are present in each uploaded attendant object.
   *
   * @param {Array<Object>} [uploadedAttendants=[]] - An array of uploaded attendant objects.
   * @returns {Array<Object>} An array of validated uploaded attendant objects.
   */
  (uploadedAttendants = []) => {
    const validatedUploadedAttendants = uploadedAttendants?.map(
      (attendant) => {
        const attendantHeaders = Object.keys(attendant)

        const missingFields = requiredFields.filter(
          (field) => !attendantHeaders.includes(field)
        )

        if (missingFields.length) {
          return {
            ...attendant,
            errors: [
              ...(attendant?.errors ?? []),
              orgAttendantsFileErrorMessagesCodes.INVALID_HEADERS,
            ],
          }
        }

        return attendant
      }
    )

    return validatedUploadedAttendants
  }

/**
 * Creates a function to validate the PIN of each uploaded attendant object.
 *
 * @returns {Function} A function that takes an array of uploaded attendants and returns the validated attendants.
 */
const validatePin =
  () =>
  /**
   * Validates the PIN of each uploaded attendant object.
   *
   * @param {Array<Object>} [uploadedAttendants=[]] - An array of uploaded attendant objects.
   * @returns {Array<Object>} An array of validated uploaded attendant objects.
   */
  (uploadedAttendants = []) => {
    const validatedUploadedAttendants = uploadedAttendants.map(
      (attendant) => {
        const pin = attendant['PIN']
        // if attendant doesn't have a pin, return the attendant
        if (!pin) {
          return attendant
        }

        const isPinNumber = /^\d+$/.test(pin)
        const isPinBetween5And6 = pin.length >= 5 && pin.length <= 6

        // check if pin is between 5 and 6 characters and is a number
        if (!isPinBetween5And6 || !isPinNumber) {
          return {
            ...attendant,
            errors: [
              ...(attendant?.errors ?? []),
              orgAttendantsFileErrorMessagesCodes.INVALID_PIN_LENGTH,
            ],
          }
        }

        return attendant
      }
    )

    return validatedUploadedAttendants
  }

/**
 * Creates a function to validate the role of each uploaded attendant object.
 *
 * @returns {Function} A function that takes an array of uploaded attendants and returns the validated attendants.
 */
const validateRole =
  () =>
  /**
   * Validates the role of each uploaded attendant object.
   *
   * @param {Array<Object>} [uploadedAttendants=[]] - An array of uploaded attendant objects.
   * @returns {Array<Object>} An array of validated uploaded attendant objects.
   */
  (uploadedAttendants = []) => {
    const validatedUploadedAttendants = uploadedAttendants.map(
      (attendant) => {
        const role = attendant['Role']
        // if attendant doesn't have a role, return the attendant
        if (!role) {
          return attendant
        }

        const isRole = UPLOADED_ORG_ATTENDANTS_ROLES.includes(role)

        // check if role is either Manager or Cashier/cart_attendant?
        if (!isRole) {
          return {
            ...attendant,
            errors: [
              ...(attendant?.errors ?? []),
              orgAttendantsFileErrorMessagesCodes.INVALID_ROLE,
            ],
          }
        }

        return attendant
      }
    )

    return validatedUploadedAttendants
  }

/**
 * Creates a function to validate that each PIN is unique among existing and uploaded attendants.
 *
 * @returns {Function} A function that takes an array of uploaded attendants and returns the validated attendants.
 */
const validatePinUnique =
  () =>
  /**
   * Validates that each PIN is unique among existing and uploaded attendants.
   *
   * @param {Array<Object>} [uploadedAttendants=[]] - An array of uploaded attendant objects.
   * @returns {Array<Object>} An array of validated uploaded attendant objects.
   */
  (uploadedAttendants = []) => {
    const validatedUploadedAttendants = uploadedAttendants.map(
      (attendant, index) => {
        const pin = attendant['PIN']
        // if attendant doesn't have a pin, return the attendant
        if (!pin) {
          return attendant
        }

        // remove current attendant from uploaded attendants
        // we don't want to compare the current attendant's pin with itself
        const uploadedAttendantsWithoutCurrentAttendant =
          uploadedAttendants.filter(
            (_, attendantIndex) => attendantIndex !== index
          )

        // get all pins from uploaded attendants
        const uploadedPins =
          uploadedAttendantsWithoutCurrentAttendant.map(
            (attendant) => attendant['PIN']
          )

        const isPinUnique = !uploadedPins.includes(pin)

        // check if pin is unique
        if (!isPinUnique) {
          return {
            ...attendant,
            errors: [
              ...(attendant?.errors ?? []),
              orgAttendantsFileErrorMessagesCodes.DUPLICATE_PIN,
            ],
          }
        }

        return attendant
      }
    )

    return validatedUploadedAttendants
  }

/**
 * Validates if there are any empty fields in the uploaded attendants.
 *
 * @returns {Function} A function that takes an array of uploaded attendants and returns the validated attendants with error messages for empty fields.
 */
const validateEmptyFields =
  () =>
  /**
   * @param {Array<Object>} uploadedAttendants - The array of uploaded attendants to validate.
   * @returns {Array<Object>} The array of validated uploaded attendants with error messages for empty fields.
   */
  (uploadedAttendants = []) => {
    const validatedUploadedAttendants = uploadedAttendants.map(
      (attendant) => {
        const emptyFields = Object.entries(attendant).filter(
          ([_, value]) => !value
        )

        if (emptyFields.length) {
          return {
            ...attendant,
            errors: [
              ...(attendant?.errors ?? []),
              orgAttendantsFileErrorMessagesCodes.EMPTY_FIELDS,
            ],
          }
        }

        return attendant
      }
    )

    return validatedUploadedAttendants
  }

const validateOrgAttendants = (uploadedAttendant) => {
  const validatedRow = pipe(
    uploadedAttendant,
    validateCorrectHeaders(UPLOADED_ORG_ATTENDANTS_REQUIRED_FIELDS),
    validatePin(),
    validateRole(),
    validatePinUnique(),
    validateEmptyFields()
  )

  return validatedRow
}

/**
 * Filters the attendants array and returns the rows with errors.
 *
 * @param {Array} attendants - The array of attendants to validate.
 * @returns {Array} - The rows with errors.
 */
const orgAttendatsRowsWithErrors = (attendants) => {
  return validateOrgAttendants(attendants).filter(
    (attendant) => attendant.errors?.length
  )
}

/**
 * Filters the attendants array and returns the rows without errors.
 *
 * @param {Array} attendants - The array of attendants to validate.
 * @returns {Array} - The rows with errors.
 */
const orgAttendatsRowsWithoutErrors = (attendants) => {
  return validateOrgAttendants(attendants).filter(
    (attendant) => !attendant?.errors?.length
  )
}

/**
 * Removes the 'errors' property from each attendant object in the array.
 *
 * @param {Array} attendants - The list of attendants.
 * @param {Object} attendants[].errors - The errors associated with the attendant.
 * @returns {Array} - A new list of attendants without the 'errors' property.
 */
const removeErrorsColumn = (attendants) => {
  return attendants?.map(({ errors, ...attendant }) => attendant)
}

const fileValidation = {
  showUploadedFileErrors,
  validateOrgAttendants,
  orgAttendatsRowsWithErrors,
  orgAttendatsRowsWithoutErrors,
  removeErrorsColumn,
  validateFileHeaders,
}

export default fileValidation
